import React from 'react'
import {
  type DeepPartial,
  NoResults,
  RecentRecords,
  recordIcons,
  RecordListCard,
  RPCDocs,
  RPCs,
  useLocalStorage,
  usePaginationState,
  UserAnPCheck,
  useRPC,
  useSetState,
  useStateDebounced,
  useTabsState,
  useUrlState,
  useUrlStateDebounced,
} from '@therms/react-modules'
import {generatePath, useHistory} from 'react-router-dom'
import {
  Button,
  Icon,
  Loading,
  Pagination,
  Tabs,
  useIsBreakpointActive,
} from '@therms/atalaya'
import {RECORDS_ROUTES} from '@src/modules/Records/constants'
import {useUpdateEffect} from 'ahooks'
import {RecordItemSearchFilters} from '@src/modules/Records/shared/RecordItemSearchFilters'
import {RecordPersonSearchFilters} from '@src/modules/Records/shared/RecordPersonSearchFilters'
import {RecordVehicleSearchFilters} from '@src/modules/Records/shared/RecordVehicleSearchFilters'
import {NewRecordButton} from '@src/modules/Records/shared/NewRecordButton'
import classnames from 'classnames'
import {HiViewGrid} from 'react-icons/hi'
import {MdViewList} from 'react-icons/md'

export type ItemSearch = DeepPartial<{
  values: {color: string; status: string; tags: string[] | any}
}>

export type PersonSearch = DeepPartial<{
  values: {
    age: {from: number; to: number}
    gender: string
    race: string
    tags: string[] | any
  }
}>

export type VehicleSearch = DeepPartial<{
  values: {
    color: string
    tags: string[] | any
    type: string
    year: {from?: number; to?: number}
  }
}>

export type RecordSearchState = DeepPartial<{
  item: ItemSearch
  person: PersonSearch
  vehicle: VehicleSearch
}>

interface RecordsListProps {
  hideNewRecordButton?: boolean
  locationId?: string
  showRecentRecords?: boolean
}

function RecordsList({
  hideNewRecordButton,
  locationId,
  showRecentRecords = true,
}: RecordsListProps) {
  const [tab, setTab] = useTabsState<any>('recordtype', 'person')
  const [searchTextValue, searchText, setSearchText] = useUrlStateDebounced(
    'recordsearch',
    '',
  )
  const {recordsPerPage, setRecordsPerPage, setSkippedRecords, skippedRecords} =
    usePaginationState('reclist', [])

  const history = useHistory()

  const isLgDisplay = useIsBreakpointActive('lg')

  const [layout, setLayout] = useLocalStorage<'list' | 'grid'>(
    'records-layout',
    'grid',
  )

  const [urlState, setUrlState] = useUrlState<RecordSearchState>(
    'recordfilters',
    {},
  )

  const [state, setState] = useSetState<RecordSearchState>(urlState)

  const [personAgeValue, personAge, setPersonAge] = useStateDebounced<{
    from?: number
    to?: number
  }>(urlState.person?.values?.age)

  const [vehicleYearValue, vehicleYear, setVehicleYear] = useStateDebounced<
    undefined | {from?: number; to?: number}
  >(urlState.vehicle?.values?.year)

  useUpdateEffect(() => {
    setState({
      person: {values: {age: personAgeValue}},
      vehicle: {values: {year: vehicleYearValue}},
    })
  }, [personAgeValue, vehicleYearValue])

  useUpdateEffect(() => {
    setUrlState(state)
  }, [state])

  useUpdateEffect(() => {
    setSkippedRecords(0)
  }, [searchText, state])

  let filters: Partial<
    RPCDocs.Records.GetRecordsList.v1.Args['fieldsFilter'] & {
      tags: string[] | any
    }
  > = {}

  if (tab === 'item')
    filters = {
      color: state?.item?.values?.color,
      status: state?.item?.values?.status,
      tags: state?.item?.values?.tags,
    }

  if (tab === 'person')
    filters = {
      ageFrom: personAge?.from,
      ageTo: personAge?.to,
      gender: state?.person?.values?.gender,
      race: state?.person?.values?.race,
      tags: state?.person?.values?.tags,
    }

  if (tab === 'vehicle')
    filters = {
      color: state?.vehicle?.values?.color,
      tags: state?.vehicle?.values?.tags,
      type: state?.vehicle?.values?.type,
      yearFrom: vehicleYear?.from,
      yearTo: vehicleYear?.to,
    }

  const {call, data, isCalling} = useRPC<
    RPCDocs.Records.GetRecordsList.v1.Args,
    RPCDocs.Records.GetRecordsList.v1.Data
  >(RPCs.Records.GetRecordsList.v1, {
    fieldsFilter: filters,
    pagination: {limit: recordsPerPage, skip: skippedRecords},
    recordTagIds: filters?.tags,
    refs: locationId ? [locationId] : undefined,
    searchText,
    typeFilter: [tab],
  })

  const handleRecordClick = (recordId: string) =>
    history.push(generatePath(RECORDS_ROUTES.VIEW_RECORD, {recordId}))

  return (
    <div className="relative">
      {showRecentRecords && (
        <>
          <div className="text-md font-medium">Recent Records</div>
          <RecentRecords onClick={handleRecordClick} />
          <div className="my-base border-border border-t" />
        </>
      )}

      <div className="gap-x-xs inline-flex w-full flex-wrap justify-between md:flex-nowrap">
        <div className="gap-x-xs inline-flex w-full items-center">
          {tab === 'person' && (
            <RecordPersonSearchFilters
              filtersState={state?.person}
              age={personAgeValue}
              searchText={searchTextValue}
              setFiltersState={setState}
              setAge={setPersonAge}
              setSearchText={setSearchText}
            />
          )}

          {tab === 'item' && (
            <RecordItemSearchFilters
              filtersState={state?.item}
              searchText={searchTextValue}
              setFiltersState={setState}
              setSearchText={setSearchText}
            />
          )}

          {tab === 'vehicle' && (
            <RecordVehicleSearchFilters
              filtersState={state?.vehicle}
              searchText={searchTextValue}
              setFiltersState={setState}
              setSearchText={setSearchText}
              setYear={setVehicleYear}
              year={vehicleYearValue}
            />
          )}
        </div>

        <div className="space-x-xxs flex w-full md:max-w-fit">
          {isLgDisplay && (
            <div>
              <Button
                onClick={() => {
                  setLayout(layout === 'grid' ? 'list' : 'grid')
                }}
                subtle
              >
                <Icon Icon={layout === 'list' ? HiViewGrid : MdViewList} />
              </Button>
            </div>
          )}

          {!hideNewRecordButton && (
            <UserAnPCheck
              check={(user) =>
                user.anp.role.ops.permissions.createRecords ||
                user.anp.role.ops.permissions.canBulkUploadRecords
              }
            >
              <div className="w-full md:w-fit">
                <NewRecordButton
                  className="w-full md:w-fit"
                  onComplete={call}
                />
              </div>
            </UserAnPCheck>
          )}
        </div>
      </div>

      <div className="my-sm relative">
        <Loading overlay visible={isCalling && !data?.records?.length} />

        <Tabs
          fullWidth
          initialTabKey={tab}
          onTabClick={(newTab) => {
            setSkippedRecords(0)
            setTab(newTab)
          }}
        >
          <Tabs.Tab tabKey="item" title="Items" />

          <Tabs.Tab tabKey="person" title="People" />

          <Tabs.Tab tabKey="vehicle" title="Vehicles" />
        </Tabs>
      </div>

      {data?.records?.length ? (
        <div
          className={classnames(
            layout === 'grid' && isLgDisplay ? 'gap-xxs grid' : 'space-y-xxs',
            'mt-base',
          )}
          style={{
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
          }}
        >
          {data.records.map((record) => (
            <RecordListCard
              description={record.description}
              idLabel={record.idLabel}
              lastActivity={record.lastActivity}
              onClick={() => handleRecordClick(record.id)}
              orientation={
                layout === 'grid' && isLgDisplay ? 'vertical' : 'horizontal'
              }
              primaryImageUrl={record.primaryImageUrl}
              tags={record.tags}
              title={record.title}
              key={record.id}
            />
          ))}
        </div>
      ) : (
        <NoResults iconName={recordIcons[tab]} text={`No ${tab} Records`} />
      )}

      {!!data?.records.length && (
        <div className="my-base">
          <Pagination
            onPageClick={setSkippedRecords}
            onSetRecordsPerPage={setRecordsPerPage}
            recordsPerPage={recordsPerPage}
            showDescription
            skippedRecords={skippedRecords}
            totalRecords={data?.total}
          />
        </div>
      )}
    </div>
  )
}

export {RecordsList}
